// SummaryOverview.jsx
// Headcount at a glance for the top of the Summary page: total and per-area
// stat cards, then gender and office splits that can be narrowed to one area.

import { useEffect, useMemo, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

import { homeCss } from '@digital_monorepo/styling';

ChartJS.register(ArcElement, Tooltip, Legend);

const NOT_SPECIFIED = 'Not specified';

// `match` is compared against the lower-cased `area` column.
const AREAS = [
  { key: 'TC', label: 'TC', match: 'tc' },
  { key: 'BC', label: 'BC', match: 'bc' },
  { key: 'TO', label: 'TO', match: 'to' },
  { key: 'Contractor', label: 'Contractors', match: 'contractor' }
];
const FILTERS = [{ key: 'All', label: 'All' }, ...AREAS];

const GENDER_ORDER = ['Female', 'Male', NOT_SPECIFIED];
const GENDER_COLOURS = { Female: '#0d8390', Male: '#25634b', [NOT_SPECIFIED]: '#c9d3cd' };
const OFFICE_COLOURS = ['#25634b', '#86bc25', '#0d8390', '#007cb0', '#43b02a', '#6fc2b4', '#9dd4cf', '#3c8a2e', '#a0dcff', '#62b5e5'];

const areaOf = p => (p.area || '').trim().toLowerCase();

const countBy = (items, keyOf) => {
  const counts = new Map();
  items.forEach(item => {
    const key = keyOf(item);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
};

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: { position: 'right', labels: { boxWidth: 12, padding: 12 } },
    tooltip: {
      callbacks: {
        label: ctx => {
          const total = ctx.dataset.data.reduce((sum, n) => sum + n, 0);
          const pct = total ? Math.round((ctx.parsed / total) * 100) : 0;
          return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
        }
      }
    }
  }
};

const SplitChart = ({ title, labels, values, colours, legendPosition }) => (
  <div className={homeCss.overviewChartCard}>
    <h3 className={homeCss.overviewChartTitle}>{title}</h3>
    {values.length ? (
      <div className={homeCss.overviewChartCanvas}>
        <Pie
          aria-label={`${title} chart`}
          role='img'
          data={{ labels, datasets: [{ data: values, backgroundColor: colours, borderColor: '#fff', borderWidth: 2 }] }}
          options={{ ...pieOptions, plugins: { ...pieOptions.plugins, legend: { ...pieOptions.plugins.legend, position: legendPosition } } }}
        />
      </div>
    ) : (
      <p className={homeCss.overviewEmpty}>No professionals</p>
    )}
  </div>
);

const SummaryOverview = ({ professionals = [], loading, error, onRetry }) => {
  const [filter, setFilter] = useState('All');
  const [legendPosition, setLegendPosition] = useState(() => window.innerWidth <= 480 ? 'bottom' : 'right');

  useEffect(() => {
    const updateLegend = () => setLegendPosition(window.innerWidth <= 480 ? 'bottom' : 'right');
    window.addEventListener('resize', updateLegend);
    return () => window.removeEventListener('resize', updateLegend);
  }, []);

  const areaCounts = useMemo(() => countBy(professionals, areaOf), [professionals]);

  const filtered = useMemo(() => {
    if (filter === 'All') return professionals;
    const { match } = AREAS.find(a => a.key === filter);
    return professionals.filter(p => areaOf(p) === match);
  }, [professionals, filter]);

  const gender = useMemo(() => {
    const counts = countBy(filtered, p => (GENDER_ORDER.includes(p.Gender) ? p.Gender : NOT_SPECIFIED));
    const labels = GENDER_ORDER.filter(g => counts.get(g));
    return { labels, values: labels.map(g => counts.get(g)), colours: labels.map(g => GENDER_COLOURS[g]) };
  }, [filtered]);

  // Largest office first, "Not specified" always last.
  const office = useMemo(() => {
    const counts = countBy(filtered, p => (p.Office || '').trim() || NOT_SPECIFIED);
    const labels = [...counts.keys()].sort((a, b) => (a === NOT_SPECIFIED) - (b === NOT_SPECIFIED) || counts.get(b) - counts.get(a) || a.localeCompare(b));
    return {
      labels,
      values: labels.map(o => counts.get(o)),
      colours: labels.map((o, i) => (o === NOT_SPECIFIED ? GENDER_COLOURS[NOT_SPECIFIED] : OFFICE_COLOURS[i % OFFICE_COLOURS.length]))
    };
  }, [filtered]);

  const filterLabel = FILTERS.find(f => f.key === filter).label;

  return (
    <section className={homeCss.chargeabilitySection} aria-labelledby='summary-overview-title'>
      <div className={homeCss.chargeabilityHeader}>
        <div>
          <h2 id='summary-overview-title' className={homeCss.chargeabilityTitle}>
            <span className={homeCss.sectionIndicator} aria-hidden='true' />
            Overview
          </h2>
          <p className={homeCss.chargeabilitySubtitle}>Professionals by area, gender and office</p>
        </div>
      </div>
      {loading ? (
        <div className={homeCss.chargeabilityState} role='status'>
          Loading professionals…
        </div>
      ) : error ? (
        <div className={homeCss.chargeabilityState} role='alert'>
          Professionals could not be loaded.
          <button className='workspace-button' onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className={homeCss.overviewStats}>
            <div className={`${homeCss.overviewStat} ${homeCss.overviewStatTotal}`}>
              <span className={homeCss.overviewStatLabel}>Headcount</span>
              <span className={homeCss.overviewStatValue} data-testid='stat-Headcount'>
                {professionals.length}
              </span>
              <span className={homeCss.overviewStatHint}>All professionals</span>
            </div>
            {AREAS.map(a => {
              const count = areaCounts.get(a.match) || 0;
              const pct = professionals.length ? Math.round((count / professionals.length) * 100) : 0;
              return (
                <div key={a.key} className={`${homeCss.overviewStat} ${homeCss.overviewStatSubset}`}>
                  <span className={homeCss.overviewStatLabel}>{a.label}</span>
                  <span className={homeCss.overviewStatValue} data-testid={`stat-${a.key}`}>
                    {count}
                  </span>
                  <span className={homeCss.overviewStatHint}>{pct}% of headcount</span>
                </div>
              );
            })}
          </div>

          <div className={homeCss.overviewFilters} role='group' aria-label='Filter charts by area'>
            {FILTERS.map(f => (
              <button
                key={f.key}
                type='button'
                aria-pressed={filter === f.key}
                className={`${homeCss.overviewFilter} ${filter === f.key ? homeCss.overviewFilterActive : ''}`}
                onClick={() => setFilter(f.key)}>
                {f.label}
                <span className={homeCss.overviewFilterCount}>{f.key === 'All' ? professionals.length : areaCounts.get(f.match) || 0}</span>
              </button>
            ))}
          </div>

          <div className={homeCss.overviewCharts}>
            <SplitChart title={`Gender split · ${filterLabel}`} {...gender} legendPosition={legendPosition} />
            <SplitChart title={`Office split · ${filterLabel}`} {...office} legendPosition={legendPosition} />
          </div>
        </>
      )}
    </section>
  );
};

export default SummaryOverview;
